import { runAppSaga } from 'app/state/store'
import { ensureMapSaved } from '../state/builderSagas'
import { Map, MapPrivacy } from 'features/maps/types'
import { isInBuilder } from '../paths'
import { put, select } from 'redux-saga/effects'
import { changeMapPrivacy } from 'features/maps/state/mapsSagas'
import { Group } from 'features/groups/types'
import { queryCacheUpdateMap } from 'features/maps/api/mapQueries'
import { updateCurrentMap } from 'features/maps/state/mapsSlice'
import { CommonPrivacy, CommonSharingScope } from 'features/common'
import { updateMap } from 'features/maps/api/maps'
import { isAutosavingEnabledSelector } from '../state/builderSlice'

export interface PublishAndSetMapPrivacyProps {
  mapId: string
  privacy: MapPrivacy
  publicPassword?: string | null
  sharingScope?: CommonSharingScope
  groups?: Group[]
  users?: Map['users']
}

export async function publishAndSetMapPrivacy(props: PublishAndSetMapPrivacyProps) {
  await runAppSaga(function* () {
    try {
      // No current map exists when sharing from the /maps page.
      const canSaveCurrentMap = isInBuilder()
      if (canSaveCurrentMap) {
        const currentMap: Map | undefined = yield select((state) => state.maps.currentMap)
        const autoSaveEnabled = yield select(isAutosavingEnabledSelector)
        if (autoSaveEnabled && currentMap?.id === props.mapId) {
          yield ensureMapSaved({ silently: true })
        }
      }
      const { groups, users, ...otherProps } = props
      yield changeMapPrivacy({
        ...otherProps,
        groupIds: groups?.map((g) => g.id),
        userIds:
          props.privacy !== CommonPrivacy.Private && props.privacy !== CommonPrivacy.Public
            ? users?.map((u) => u.id)
            : undefined
      })
      const { mapId: id, privacy, sharingScope, publicPassword } = props

      const mapPatch: Partial<Map> = {
        id,
        privacy,
        sharingScope,
        ...(groups && { groups }),
        ...(users && { users }),
        ...(publicPassword !== undefined && {
          publishedWithPassword: Boolean(publicPassword),
          publicMapPassword: publicPassword ?? undefined
        })
      }
      if (canSaveCurrentMap) {
        yield put(updateCurrentMap(mapPatch))
      }
      queryCacheUpdateMap(mapPatch)
    } catch (error) {
      throw new Error(`publishAndSetMapPrivacy failed: ${error}`)
    }
  })
}

export async function updateMapAndPublish(mapPatch: Partial<Map> & { id: Map['id'] }) {
  await runAppSaga(function* () {
    try {
      const updatedMap: Map = yield updateMap(mapPatch)
      const shouldUpdateCurrentMap = isInBuilder()
      if (shouldUpdateCurrentMap) {
        yield changeMapPrivacy({
          mapId: updatedMap.id,
          privacy: updatedMap.privacy as CommonPrivacy,
          sharingScope: updatedMap.privacy === MapPrivacy.Shared ? updatedMap.sharingScope : undefined
        })
        yield put(updateCurrentMap(mapPatch))
      }
      queryCacheUpdateMap(mapPatch)
    } catch (error) {
      throw new Error(`updateMapAndPublish failed: ${error}`)
    }
  })
}
